import React, { useState } from "react";

// Small, inline (non-modal) accordion preview of how a question will appear
// to a respondent, based on its current type/rows/columns/settings.
// Renders full-width right under the question header when opened - it
// pushes content down like an accordion, it does not float/overlay.
//
// THEME NOTE:
// All colors now come from CSS custom properties (with sensible fallbacks
// so this still looks fine if the vars aren't defined). Point these at the
// same variable names your ThemeProvider / ChartThemeContext sets on the
// root (e.g. document.documentElement.style.setProperty('--theme-primary', ...)).
// If your theme system uses different var names, just rename the
// `var(--theme-x, fallback)` references below to match.

const badge = {
  fontSize: "0.75rem",
  fontWeight: 700,
  color: "var(--theme-primary, #4338ca)",
  background: "var(--theme-primary-bg, #eef2ff)",
  padding: "2px 8px",
  borderRadius: "6px",
  flexShrink: 0,
};

const optionRowStyle = (isSelected) => ({
  display: "flex",
  alignItems: "center",
  gap: "12px",
  padding: "13px 18px",
  borderRadius: "10px",
  cursor: "pointer",
  border: isSelected
    ? "1.5px solid var(--theme-primary-light, #6366f1)"
    : "1px solid var(--theme-border, #e5e7eb)",
  background: isSelected ? "var(--theme-primary-bg, #eef2ff)" : "var(--theme-surface, #ffffff)",
  fontWeight: isSelected ? 600 : 400,
  fontSize: "0.98rem",
  color: "var(--theme-text, #1f2937)",
  transition: "background 0.15s ease, border-color 0.15s ease",
});

const inputBaseStyle = {
  width: "100%",
  maxWidth: "320px",
  padding: "10px 14px",
  fontSize: "0.95rem",
  border: "1px solid var(--theme-border, #d1d5db)",
  borderRadius: "8px",
  background: "var(--theme-surface, #fff)",
  color: "var(--theme-text-muted, #374151)",
};

const QuestionAppearancePreview = ({ question }) => {
  const [selected, setSelected] = useState(null);

  if (!question) return null;

  const type = question.type || "";
  const rows = question.rows || [];
  const columns = question.columns || [];
  const isGridType = type.includes("Grid");
  const isMultiType = type.startsWith("Multiple") || type === "MultiGrid";

  const renderRowOption = (row, i) => {
    const num = row.index || i + 1;
    const isSelected = selected === num;
    return (
      <div
        key={row.index || i}
        onClick={() => setSelected(isSelected ? null : num)}
        style={optionRowStyle(isSelected)}
      >
        <span style={badge}>[{num}]</span>
        <span dangerouslySetInnerHTML={{ __html: row.text || "—" }} />
      </div>
    );
  };

  const renderBody = () => {
    if (type === "Intro") {
      return (
        <button
          disabled
          style={{
            padding: "10px 32px",
            background: "var(--theme-primary, #2563eb)",
            color: "var(--theme-on-primary, #fff)",
            border: "none",
            borderRadius: "6px",
            fontSize: "0.95rem",
            fontWeight: 500,
            cursor: "default",
          }}
        >
          CONTINUE
        </button>
      );
    }

    if (type === "Numeric" || type === "NumericGrid" || type === "Numeric2DGrid") {
      return <input type="number" readOnly placeholder="Enter your answer" style={inputBaseStyle} />;
    }

    if (type === "String" || type === "StringGrid") {
      return (
        <textarea
          readOnly
          placeholder="Respondent's answer will appear here..."
          rows={3}
          style={{ ...inputBaseStyle, maxWidth: "100%", resize: "none" }}
        />
      );
    }

    if (isGridType && rows.length > 0 && columns.length > 0) {
      const gridTemplateColumns = `minmax(140px, 1.4fr) repeat(${columns.length}, minmax(100px, 1fr))`;
      return (
        <div style={{ overflowX: "auto", border: "1px solid var(--theme-border, #e5e7eb)", borderRadius: "8px" }}>
          <div style={{ minWidth: `${140 + columns.length * 100}px` }}>
            {/* Header row */}
            <div style={{ display: "grid", gridTemplateColumns, background: "var(--theme-surface-muted, #f3f4f6)" }}>
              <div style={{ padding: "10px 12px" }} />
              {columns.map((col, i) => (
                <div
                  key={col.index || i}
                  style={{
                    padding: "10px 8px",
                    textAlign: "center",
                    fontSize: "0.85rem",
                    fontWeight: 600,
                    color: "var(--theme-text-muted, #374151)",
                    whiteSpace: "normal",
                    wordBreak: "break-word",
                    borderLeft: "1px solid var(--theme-border, #e5e7eb)",
                  }}
                >
                  <span dangerouslySetInnerHTML={{ __html: col.text || "—" }} />
                </div>
              ))}
            </div>
            {/* Data rows */}
            {rows.map((row, i) => (
              <div
                key={row.index || i}
                style={{
                  display: "grid",
                  gridTemplateColumns,
                  background:
                    i % 2 === 0
                      ? "var(--theme-surface, #ffffff)"
                      : "var(--theme-surface-alt, #f9fafb)",
                  borderTop: "1px solid var(--theme-border, #e5e7eb)",
                }}
              >
                <div
                  style={{
                    padding: "10px 12px",
                    fontSize: "0.9rem",
                    color: "var(--theme-text, #1f2937)",
                    whiteSpace: "normal",
                    wordBreak: "break-word",
                  }}
                >
                  <span dangerouslySetInnerHTML={{ __html: row.text || "—" }} />
                </div>
                {columns.map((col, ci) => (
                  <div
                    key={col.index || ci}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      padding: "10px",
                      borderLeft: "1px solid var(--theme-border, #e5e7eb)",
                    }}
                  >
                    <input type={isMultiType ? "checkbox" : "radio"} readOnly style={{ width: "16px", height: "16px" }} />
                  </div>
                ))}
              </div>
            ))}
          </div>
        </div>
      );
    }

    if (rows.length > 0) {
      return (
        <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
          {rows.map((row, i) => renderRowOption(row, i))}
        </div>
      );
    }

    return (
      <div style={{ color: "var(--theme-text-faint, #9ca3af)", fontSize: "0.9rem", padding: "8px 0" }}>
        No answer options configured yet for this question.
      </div>
    );
  };

  return (
    <div style={{ width: "100%" }}>
      <div style={{ display: "flex", alignItems: "center", gap: "14px", marginBottom: "16px", flexWrap: "wrap" }}>
        <span
          style={{
            background: "var(--theme-primary, #4338ca)",
            color: "var(--theme-on-primary, #fff)",
            fontWeight: 700,
            padding: "6px 16px",
            borderRadius: "20px",
            fontSize: "0.9rem",
          }}
        >
          {question.Question_number || "Q?"}
        </span>
        <span
          style={{ fontWeight: 700, color: "var(--theme-primary-dark, #1e3a8a)", fontSize: "1.1rem" }}
          dangerouslySetInnerHTML={{ __html: question.Qtitle || "Untitled question" }}
        />
      </div>
      {renderBody()}
    </div>
  );
};

export default QuestionAppearancePreview;